import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaTrash,
  FaEdit,
  FaInfoCircle,
  FaCalendarCheck,
} from "react-icons/fa";
import "./Style.css";
import { BackOfficeHeader } from "./GameSessionHeader";

const EVENT_TYPES = {
  CASH: 1,
  TOURNAMENT: 2,
  BOTH: 3,
};

const formatDateLocal = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  const datePart = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    const [year, month, day] = datePart.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
};

const formatDisplayDate = (value) => {
  if (!value) return "";
  const date = toLocalDate(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const eventTypeFromFlags = (hasCash, hasTournament) => {
  if (hasCash && hasTournament) return EVENT_TYPES.BOTH;
  if (hasTournament) return EVENT_TYPES.TOURNAMENT;
  return EVENT_TYPES.CASH;
};

const gameHasType = (game, eventType) =>
  Number(game?.type) === EVENT_TYPES.BOTH || Number(game?.type) === eventType;

const eventTypeLabel = (eventType) =>
  Number(eventType) === EVENT_TYPES.CASH ? "Cash" : "Tournament";

const statusLabel = (status) => {
  if (status === "accepted") return "Scheduled";
  if (status === "rejected") return "Rejected";
  return "Pending";
};

const statusColor = (status) => {
  if (status === "accepted") return "#48bb78";
  if (status === "rejected") return "#f56565";
  return "#FFBF00";
};

const initialEventForm = {
  title: "",
  description: "",
  scheduledAt: "18:00",
  hasCash: true,
  hasTournament: false,
  allowCashRequests: false,
  allowTournamentRequests: false,
  notifyDealers: true,
};

export default function Schedule() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);
  const canManage = permission?.includes("H");

  const [dealers, setDealers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [scheduledGames, setScheduledGames] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [eventForm, setEventForm] = useState(initialEventForm);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [hoveredDayKey, setHoveredDayKey] = useState("");
  const [pendingDeleteGame, setPendingDeleteGame] = useState(null);
  const [dealerSelections, setDealerSelections] = useState({
    [EVENT_TYPES.CASH]: "",
    [EVENT_TYPES.TOURNAMENT]: "",
  });

  useEffect(() => {
    if (!token) {
      navigate(`/`);
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealer_dashboard`);
      return;
    } else if (!permission?.includes("V")) {
      navigate("/");
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token, cardroom, navigate, dealerMode, permission]);

  useEffect(() => {
    loadScheduleData();
  }, [currentDate]);

  const loadScheduleData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const start = formatDateLocal(firstDay);
      const end = formatDateLocal(lastDay);

      const [gameRes, dealerRes, requestRes] = await Promise.all([
        axios.get(`/${cardroom}/api/schedule/games`, { params: { start, end } }),
        axios.get(`/${cardroom}/api/schedule/dealers`),
        axios.get(`/${cardroom}/api/schedule/requests`),
      ]);

      setScheduledGames(gameRes.data || []);
      setDealers(dealerRes.data || []);
      setRequests(requestRes.data || []);
    } catch (err) {
      console.error("Failed to load schedule data", err);
    }
  };

  const refreshRequests = async () => {
    try {
      const { data } = await axios.get(`/${cardroom}/api/schedule/requests`);
      setRequests(data || []);
    } catch (err) {
      console.error("Failed to refresh schedule requests", err);
    }
  };

  const resetEventForm = (day = selectedDay) => {
    setEventForm({
      ...initialEventForm,
      title: "",
      description: "",
      scheduledAt: "18:00",
      hasCash: true,
      hasTournament: false,
      allowCashRequests: false,
      allowTournamentRequests: false,
      notifyDealers: true,
    });
    setSelectedDay(day);
  };

  const populateEventForm = (game) => {
    const hasCash = gameHasType(game, EVENT_TYPES.CASH);
    const hasTournament = gameHasType(game, EVENT_TYPES.TOURNAMENT);
    setEventForm({
      title: game?.game_info || "",
      description: game?.description || "",
      scheduledAt: game?.scheduled_at || "18:00",
      hasCash,
      hasTournament,
      allowCashRequests: Boolean(game?.allow_cash_requests),
      allowTournamentRequests: Boolean(game?.allow_tournament_requests),
      notifyDealers: game?.notify_dealers === undefined ? true : Boolean(game.notify_dealers),
    });
    setSelectedDay(game?.day ? toLocalDate(game.day) : null);
  };

  const buildEventPayload = () => {
    const hasCash = Boolean(eventForm.hasCash);
    const hasTournament = Boolean(eventForm.hasTournament);
    return {
      game_info: eventForm.title.trim(),
      description: eventForm.description.trim(),
      scheduled_at: eventForm.scheduledAt,
      day: formatDateLocal(selectedDay),
      type: eventTypeFromFlags(hasCash, hasTournament),
      allow_cash_requests: hasCash && eventForm.allowCashRequests ? 1 : 0,
      allow_tournament_requests:
        hasTournament && eventForm.allowTournamentRequests ? 1 : 0,
      notify_dealers: eventForm.notifyDealers ? 1 : 0,
    };
  };

  const validateEventForm = () => {
    if (!selectedDay) return "Choose a day";
    if (!eventForm.title.trim()) return "Title is required";
    if (!eventForm.scheduledAt) return "Time is required";
    if (!eventForm.hasCash && !eventForm.hasTournament) {
      return "Select cash, tournament, or both";
    }
    return "";
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    const validationError = validateEventForm();
    if (validationError || isSubmitting) {
      if (validationError) alert(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      const { data } = await axios.post(
        `/${cardroom}/api/schedule/games`,
        buildEventPayload(),
      );
      setScheduledGames((prev) => [...prev, data]);
      setShowCreateModal(false);
      resetEventForm(null);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to create scheduled event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openCreateModal = (day) => {
    if (!canManage) return;
    resetEventForm(day);
    setShowCreateModal(true);
  };

  const openDetailModal = async (gameId) => {
    setDetailLoading(true);
    setDetail(null);
    setIsEditingDetail(false);
    try {
      const { data } = await axios.get(
        `/${cardroom}/api/schedule/games/${gameId}/details`,
      );
      setDetail(data);
      populateEventForm(data.game);
    } catch (err) {
      alert(err.response?.data?.error || "Failed to load scheduled event");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSaveDetail = async (e) => {
    e.preventDefault();
    const validationError = validateEventForm();
    if (validationError || !detail?.game?.id || isSubmitting) {
      if (validationError) alert(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      const { data } = await axios.patch(
        `/${cardroom}/api/schedule/games/${detail.game.id}`,
        buildEventPayload(),
      );
      setScheduledGames((prev) =>
        prev.map((game) => (game.id === data.id ? data : game)),
      );
      setDetail((prev) => (prev ? { ...prev, game: data } : prev));
      setIsEditingDetail(false);
      await refreshRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to save scheduled event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGame = async (id) => {
    try {
      setIsSubmitting(true);
      await axios.delete(`/${cardroom}/api/schedule/games/${id}`);
      setScheduledGames((prev) => prev.filter((game) => game.id !== id));
      setDetail(null);
      setPendingDeleteGame(null);
      await refreshRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete scheduled event");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDeleteModal = (game) => {
    setPendingDeleteGame(game);
    setDetail(null);
    setIsEditingDetail(false);
  };

  const handleInviteDealer = async (eventType, mode) => {
    const dealerId = dealerSelections[eventType];
    if (!dealerId || !detail?.game?.id || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const { data } = await axios.post(
        `/${cardroom}/api/schedule/games/${detail.game.id}/dealer-invites`,
        {
          dealer_id: dealerId,
          event_type: eventType,
          mode,
        },
      );
      setDetail((prev) => {
        const others = (prev?.invites || []).filter((invite) => invite.id !== data.id);
        return { ...prev, invites: [...others, data] };
      });
      setDealerSelections((prev) => ({ ...prev, [eventType]: "" }));
      await refreshRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update dealer schedule");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInviteStatus = async (inviteId, status) => {
    try {
      const { data } = await axios.patch(
        `/${cardroom}/api/schedule/dealer-invites/${inviteId}`,
        { status },
      );
      setDetail((prev) => ({
        ...prev,
        invites: (prev?.invites || []).map((invite) =>
          invite.id === data.id ? data : invite,
        ),
      }));
      await refreshRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to update request");
    }
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`/${cardroom}/api/schedule/dealer-invites/${inviteId}`);
      setDetail((prev) => ({
        ...prev,
        invites: (prev?.invites || []).filter((invite) => invite.id !== inviteId),
      }));
      await refreshRequests();
    } catch (err) {
      alert(err.response?.data?.error || "Failed to remove dealer");
    }
  };

  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, 1);
    const days = [];

    const firstDayIndex = date.getDay();
    const prevLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayIndex; i > 0; i--) {
      days.push({ day: prevLastDay - i + 1, month: month - 1, year, padding: true });
    }

    const totalDays = new Date(year, month + 1, 0).getDate();
    for (let i = 1; i <= totalDays; i++) {
      days.push({ day: i, month, year, padding: false });
    }

    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ day: i, month: month + 1, year, padding: true });
    }

    return days;
  }, [currentDate]);

  const changeMonth = (offset) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + offset,
      1,
    );
    setCurrentDate(newDate);
  };

  const isToday = (d, m, y) => {
    const today = new Date();
    return d === today.getDate() && m === today.getMonth() && y === today.getFullYear();
  };

  const getGameForDay = (d, m, y) => {
    const dayStr = formatDateLocal(new Date(y, m, d));
    return scheduledGames.find((game) => formatDateLocal(toLocalDate(game.day)) === dayStr);
  };

  const renderTypeBadges = (game) => (
    <div style={{ display: "flex", gap: "0.35rem", flexWrap: "wrap" }}>
      {gameHasType(game, EVENT_TYPES.CASH) && (
        <span style={styles.typeBadge}>Cash</span>
      )}
      {gameHasType(game, EVENT_TYPES.TOURNAMENT) && (
        <span style={styles.typeBadge}>Tournament</span>
      )}
    </div>
  );

  const renderEventTypeSection = (eventType) => {
    const invites = (detail?.invites || []).filter(
      (invite) => Number(invite.event_type) === eventType,
    );
    const availableDealers = dealers.filter(
      (dealer) =>
        !invites.some(
          (invite) =>
            Number(invite.dealer_id) === Number(dealer.id) &&
            invite.status !== "rejected",
        ),
    );

    return (
      <section key={eventType} style={styles.manageSection}>
        <div style={styles.manageHeader}>
          <h4 style={styles.manageTitle}>{eventTypeLabel(eventType)}</h4>
          <span style={styles.countBadge}>
            {invites.filter((invite) => invite.status === "accepted").length} scheduled
          </span>
        </div>

        {canManage && (
          <div style={styles.dealerActionRow}>
            <select
              className="game-input"
              value={dealerSelections[eventType]}
              onChange={(e) =>
                setDealerSelections((prev) => ({
                  ...prev,
                  [eventType]: e.target.value,
                }))
              }
            >
              <option value="">Select connected dealer</option>
              {availableDealers.map((dealer) => (
                <option key={dealer.id} value={dealer.id}>
                  {dealer.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="game-btn game-btn--secondary"
              onClick={() => handleInviteDealer(eventType, "invite")}
              disabled={!dealerSelections[eventType] || isSubmitting}
            >
              Invite
            </button>
            <button
              type="button"
              className="game-btn game-btn--primary"
              onClick={() => handleInviteDealer(eventType, "schedule")}
              disabled={!dealerSelections[eventType] || isSubmitting}
            >
              Schedule
            </button>
          </div>
        )}

        <div style={styles.inviteList}>
          {invites.length === 0 ? (
            <p className="game-empty" style={{ margin: 0 }}>
              No dealers yet
            </p>
          ) : (
            invites.map((invite) => (
              <div key={invite.id} style={styles.inviteRow}>
                <div>
                  <strong>{invite.dealer_name}</strong>
                  <div style={styles.inviteMeta}>
                    {invite.source === "dealer_request" ? "Dealer request" : "Admin"}
                  </div>
                </div>
                <div style={styles.inviteActions}>
                  <span style={{ ...styles.statusPill, color: statusColor(invite.status) }}>
                    {statusLabel(invite.status)}
                  </span>
                  {canManage &&
                    invite.status === "pending" &&
                    invite.source === "dealer_request" && (
                    <>
                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--primary"
                        onClick={() => handleInviteStatus(invite.id, "accepted")}
                      >
                        Accept
                      </button>
                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--secondary"
                        onClick={() => handleInviteStatus(invite.id, "rejected")}
                      >
                        Reject
                      </button>
                    </>
                  )}
                  {canManage &&
                    invite.status === "pending" &&
                    invite.source !== "dealer_request" && (
                      <button
                        type="button"
                        className="game-btn game-btn--sm game-btn--secondary"
                        onClick={() => handleDeleteInvite(invite.id)}
                      >
                        Cancel
                      </button>
                    )}
                  {canManage && invite.status === "accepted" && (
                    <button
                      type="button"
                      className="game-btn game-btn--sm game-btn--secondary"
                      onClick={() => handleDeleteInvite(invite.id)}
                    >
                      Remove
                    </button>
                  )}
                  {canManage && invite.status === "rejected" && (
                    <button
                      type="button"
                      className="game-btn game-btn--sm game-btn--primary"
                      onClick={() => handleInviteStatus(invite.id, "accepted")}
                    >
                      Accept
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    );
  };

  const renderEventFields = (readOnly = false) => (
    <>
      <label className="game-label">Title</label>
      <input
        className="game-input"
        value={eventForm.title}
        onChange={(e) => setEventForm((prev) => ({ ...prev, title: e.target.value }))}
        placeholder="ROE and PLO"
        disabled={readOnly}
        required
      />

      <label className="game-label">Description</label>
      <textarea
        className="game-input"
        value={eventForm.description}
        onChange={(e) =>
          setEventForm((prev) => ({ ...prev, description: e.target.value }))
        }
        placeholder="Optional details for dealers"
        disabled={readOnly}
        rows={3}
        style={{ resize: "vertical" }}
      />

      <label className="game-label">Time</label>
      <select
        className="game-input"
        value={eventForm.scheduledAt}
        onChange={(e) =>
          setEventForm((prev) => ({ ...prev, scheduledAt: e.target.value }))
        }
        disabled={readOnly}
        required
      >
        {Array.from({ length: 48 }, (_, i) => {
          const h = Math.floor(i / 2).toString().padStart(2, "0");
          const m = i % 2 === 0 ? "00" : "30";
          const time = `${h}:${m}`;
          return (
            <option key={time} value={time}>
              {time}
            </option>
          );
        })}
      </select>

      <label className="game-label">Type</label>
      <div style={styles.toggleGrid}>
        <label style={styles.checkTile}>
          <input
            type="checkbox"
            checked={eventForm.hasCash}
            disabled={readOnly}
            onChange={(e) =>
              setEventForm((prev) => ({
                ...prev,
                hasCash: e.target.checked,
                allowCashRequests: e.target.checked ? prev.allowCashRequests : false,
              }))
            }
          />
          Cash
        </label>
        <label style={styles.checkTile}>
          <input
            type="checkbox"
            checked={eventForm.hasTournament}
            disabled={readOnly}
            onChange={(e) =>
              setEventForm((prev) => ({
                ...prev,
                hasTournament: e.target.checked,
                allowTournamentRequests: e.target.checked
                  ? prev.allowTournamentRequests
                  : false,
              }))
            }
          />
          Tournament
        </label>
      </div>

      <label className="game-label" style={styles.infoLabel}>
        Dealer request visibility
        <FaInfoCircle
          title="If requests are disabled for a type, dealers will not see that event type on the mobile schedule unless they are already invited or scheduled."
          style={{ color: "#FFBF00" }}
        />
      </label>
      <div style={styles.toggleGrid}>
        <label style={{ ...styles.checkTile, opacity: eventForm.hasCash ? 1 : 0.45 }}>
          <input
            type="checkbox"
            checked={eventForm.allowCashRequests}
            disabled={readOnly || !eventForm.hasCash}
            onChange={(e) =>
              setEventForm((prev) => ({
                ...prev,
                allowCashRequests: e.target.checked,
              }))
            }
          />
          Allow cash requests
        </label>
        <label
          style={{
            ...styles.checkTile,
            opacity: eventForm.hasTournament ? 1 : 0.45,
          }}
        >
          <input
            type="checkbox"
            checked={eventForm.allowTournamentRequests}
            disabled={readOnly || !eventForm.hasTournament}
            onChange={(e) =>
              setEventForm((prev) => ({
                ...prev,
                allowTournamentRequests: e.target.checked,
              }))
            }
          />
          Allow tournament requests
        </label>
      </div>

      <label className="game-label" style={styles.infoLabel}>
        Notifications
        <FaInfoCircle
          title="When Notify Dealers is off, scheduling, invite, and cancellation emails will not be sent for this event."
          style={{ color: "#FFBF00" }}
        />
      </label>
      <label style={styles.checkTile}>
        <input
          type="checkbox"
          checked={eventForm.notifyDealers}
          disabled={readOnly}
          onChange={(e) =>
            setEventForm((prev) => ({
              ...prev,
              notifyDealers: e.target.checked,
            }))
          }
        />
        Notify dealers
      </label>
    </>
  );

  return (
    <div className="game">
      <BackOfficeHeader
        activePage="schedule"
        cardroom={cardroom}
        centerTitle="Schedule"
        dealerMode={dealerMode}
        title="Schedule"
      />

      <div
        className="game-container"
        style={{
          display: "grid",
          gridTemplateColumns: "300px 1fr",
          gap: "1.5rem",
          alignItems: "start",
        }}
      >
        <aside style={{ display: "grid", gap: "1rem" }}>
          <section className="game-section">
            <h2 className="game-section__title" style={{ marginTop: 0 }}>
              Connected Dealers
            </h2>
            <div style={styles.sideList}>
              {dealers.length === 0 ? (
                <p className="game-empty">No connected dealers</p>
              ) : (
                dealers.map((dealer) => (
                  <div key={dealer.id} style={styles.dealerRow}>
                    <div style={styles.dealerAvatar}>{dealer.name?.charAt(0) || "D"}</div>
                    <div>
                      <strong>{dealer.name}</strong>
                      <div style={styles.inviteMeta}>{dealer.email || dealer.username}</div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>

          <section className="game-section">
            <h2 className="game-section__title" style={{ marginTop: 0 }}>
              Requests
            </h2>
            <div style={styles.sideList}>
              {requests.length === 0 ? (
                <p className="game-empty">No pending requests</p>
              ) : (
                requests.map((request) => (
                  <button
                    key={request.id}
                    type="button"
                    style={styles.requestButton}
                    onClick={() => openDetailModal(request.scheduled_game_id)}
                  >
                    <div>
                      <strong>{request.dealer_name}</strong>
                      <div style={styles.inviteMeta}>
                        {eventTypeLabel(request.event_type)} - {formatDisplayDate(request.day)}
                      </div>
                    </div>
                    <FaCalendarCheck style={{ color: "#FFBF00" }} />
                  </button>
                ))
              )}
            </div>
          </section>
        </aside>

        <section className="game-section">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2 className="game-section__title" style={{ margin: 0 }}>
              {currentDate.toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </h2>
            <div style={{ display: "flex", gap: "0.5rem" }}>
              <button
                className="game-btn game-btn--secondary"
                onClick={() => changeMonth(-1)}
              >
                <FaChevronLeft />
              </button>
              <button
                className="game-btn game-btn--secondary"
                onClick={() => setCurrentDate(new Date())}
              >
                Today
              </button>
              <button
                className="game-btn game-btn--secondary"
                onClick={() => changeMonth(1)}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>

          <div style={styles.calendarGrid}>
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} style={styles.weekday}>
                {day}
              </div>
            ))}
            {daysInMonth.map((d, index) => {
              const game = getGameForDay(d.day, d.month, d.year);
              const today = isToday(d.day, d.month, d.year);
              const dayKey = `${d.year}-${d.month}-${d.day}`;
              const isHovered = hoveredDayKey === dayKey && !d.padding;
              const dayButtonStyle = {
                ...styles.dayButton,
                backgroundColor: d.padding
                  ? "#1a202c"
                  : isHovered
                    ? "#374151"
                    : "#2d3748",
                opacity: d.padding ? 0.5 : 1,
                border: today ? "2px solid #ed8936" : "none",
                boxShadow: isHovered ? "inset 0 0 0 2px #FFBF00" : "none",
                cursor: d.padding || (!canManage && !game) ? "default" : "pointer",
              };

              return (
                <button
                  key={index}
                  type="button"
                  style={dayButtonStyle}
                  disabled={d.padding || (!canManage && !game)}
                  onMouseEnter={() => setHoveredDayKey(dayKey)}
                  onMouseLeave={() => setHoveredDayKey("")}
                  onClick={() => {
                    if (d.padding) return;
                    if (game) {
                      openDetailModal(game.id);
                    } else {
                      openCreateModal(new Date(d.year, d.month, d.day));
                    }
                  }}
                >
                  <span style={styles.dayNumber}>{d.day}</span>
                  {game ? (
                    <div style={styles.fullDayEvent}>
                      <div style={styles.eventTime}>{game.scheduled_at}</div>
                      <div style={styles.eventTitle}>{game.game_info}</div>
                      {renderTypeBadges(game)}
                    </div>
                  ) : (
                    !d.padding && <span style={styles.emptyDayText}>Click to schedule</span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      </div>

      {showCreateModal && (
        <>
          <div className="game-modal-overlay" onClick={() => setShowCreateModal(false)} />
          <div className="game-modal">
            <div className="game-modal__header">
              <h3 className="game-modal__title">
                Schedule Event for {selectedDay?.toLocaleDateString()}
              </h3>
            </div>
            <div className="game-modal__body">
              <form className="game-form-vertical" onSubmit={handleCreateEvent}>
                {renderEventFields(false)}
                <div className="game-modal__footer">
                  <button
                    type="button"
                    className="game-btn game-btn--ghost"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="game-btn game-btn--primary"
                    disabled={isSubmitting}
                  >
                    Create Event
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {(detail || detailLoading) && (
        <>
          <div
            className="game-modal-overlay"
            onClick={() => {
              setDetail(null);
              setIsEditingDetail(false);
            }}
          />
          <div className="game-modal" style={{ maxWidth: "760px" }}>
            <div className="game-modal__header" style={styles.modalHeader}>
              <div>
                <h3 className="game-modal__title">
                  {detailLoading ? "Loading event..." : detail?.game?.game_info}
                </h3>
                {detail?.game && (
                  <p style={styles.modalSubtitle}>
                    {formatDisplayDate(detail.game.day)} at {detail.game.scheduled_at}
                  </p>
                )}
              </div>
              <button
                type="button"
                className="game-icon-btn"
                onClick={() => {
                  setDetail(null);
                  setIsEditingDetail(false);
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="game-modal__body">
              {detailLoading ? (
                <p className="game-empty">Loading...</p>
              ) : (
                <>
                  <form className="game-form-vertical" onSubmit={handleSaveDetail}>
                    <div style={styles.detailTopActions}>
                      <div>{renderTypeBadges(detail.game)}</div>
                      {canManage && (
                        <div style={{ display: "flex", gap: "0.5rem" }}>
                          <button
                            type="button"
                            className="game-btn game-btn--secondary"
                            onClick={() => setIsEditingDetail((value) => !value)}
                          >
                            <FaEdit /> {isEditingDetail ? "Stop Editing" : "Edit"}
                          </button>
                          <button
                            type="button"
                            className="game-btn game-btn--secondary"
                            style={{ color: "#f56565" }}
                            onClick={() => openDeleteModal(detail.game)}
                          >
                            <FaTrash /> Delete
                          </button>
                        </div>
                      )}
                    </div>

                    {isEditingDetail ? (
                      <>
                        {renderEventFields(false)}
                        <div className="game-modal__footer">
                          <button
                            type="button"
                            className="game-btn game-btn--ghost"
                            onClick={() => {
                              populateEventForm(detail.game);
                              setIsEditingDetail(false);
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="game-btn game-btn--primary"
                            disabled={isSubmitting}
                          >
                            Save
                          </button>
                        </div>
                      </>
                    ) : (
                      detail.game.description && (
                        <p style={styles.descriptionText}>{detail.game.description}</p>
                      )
                    )}
                  </form>

                  <div style={styles.manageGrid}>
                    {gameHasType(detail.game, EVENT_TYPES.CASH) &&
                      renderEventTypeSection(EVENT_TYPES.CASH)}
                    {gameHasType(detail.game, EVENT_TYPES.TOURNAMENT) &&
                      renderEventTypeSection(EVENT_TYPES.TOURNAMENT)}
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {pendingDeleteGame && (
        <>
          <div className="game-modal-overlay" onClick={() => setPendingDeleteGame(null)} />
          <div className="game-modal" style={styles.deleteModal}>
            <div className="game-modal__header">
              <h3 className="game-modal__title">Delete Scheduled Event</h3>
            </div>
            <div className="game-modal__body">
              <p style={styles.deleteWarning}>
                Delete <strong>{pendingDeleteGame.game_info}</strong> on{" "}
                {formatDisplayDate(pendingDeleteGame.day)}? This will remove all dealer
                requests, invites, and scheduled dealers for this event.
              </p>
              <div className="game-modal__footer">
                <button
                  type="button"
                  className="game-btn game-btn--ghost"
                  onClick={() => setPendingDeleteGame(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="game-btn game-btn--secondary"
                  style={{ color: "#f56565", borderColor: "#f56565" }}
                  onClick={() => handleDeleteGame(pendingDeleteGame.id)}
                  disabled={isSubmitting}
                >
                  <FaTrash /> Delete Event
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  sideList: {
    display: "grid",
    gap: "0.5rem",
  },
  dealerRow: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0.75rem",
    border: "1px solid #4a5568",
    borderRadius: "6px",
    backgroundColor: "#1a202c",
  },
  dealerAvatar: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    backgroundColor: "#FFBF00",
    color: "#1a202c",
    fontWeight: 900,
  },
  inviteMeta: {
    color: "#a0aec0",
    fontSize: "0.78rem",
    marginTop: "0.15rem",
  },
  requestButton: {
    border: "1px solid #4a5568",
    backgroundColor: "#1a202c",
    color: "white",
    borderRadius: "6px",
    padding: "0.75rem",
    textAlign: "left",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "1px",
    backgroundColor: "#4a5568",
    borderRadius: "8px",
    overflow: "hidden",
    border: "1px solid #4a5568",
  },
  weekday: {
    backgroundColor: "#2d3748",
    padding: "0.5rem",
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "0.8rem",
    color: "#a0aec0",
  },
  dayButton: {
    minHeight: "140px",
    padding: "0.5rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    color: "white",
    textAlign: "left",
  },
  dayNumber: {
    fontWeight: "bold",
    fontSize: "0.9rem",
    marginBottom: "0.5rem",
  },
  fullDayEvent: {
    flex: 1,
    minHeight: "92px",
    backgroundColor: "#FFBF0022",
    border: "1px solid #FFBF00",
    borderLeft: "4px solid #FFBF00",
    borderRadius: "6px",
    padding: "0.55rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: "0.4rem",
  },
  eventTime: {
    color: "#FFBF00",
    fontWeight: 900,
    fontSize: "0.8rem",
  },
  eventTitle: {
    fontWeight: 900,
    fontSize: "0.92rem",
    lineHeight: 1.2,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  typeBadge: {
    display: "inline-flex",
    alignItems: "center",
    border: "1px solid #FFBF00",
    color: "#FFBF00",
    borderRadius: "4px",
    padding: "0.12rem 0.35rem",
    fontSize: "0.68rem",
    fontWeight: 900,
    textTransform: "uppercase",
  },
  emptyDayText: {
    marginTop: "auto",
    color: "#718096",
    fontSize: "0.78rem",
  },
  toggleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "0.75rem",
  },
  checkTile: {
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
    minHeight: "44px",
    padding: "0.75rem",
    border: "1px solid #4a5568",
    borderRadius: "6px",
    backgroundColor: "#1a202c",
    color: "white",
    fontWeight: 800,
  },
  infoLabel: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  modalHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: "1rem",
  },
  modalSubtitle: {
    margin: "0.35rem 0 0",
    color: "#a0aec0",
    fontSize: "0.9rem",
  },
  detailTopActions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1rem",
    marginBottom: "1rem",
  },
  descriptionText: {
    margin: "0 0 1rem",
    padding: "0.85rem",
    color: "#d8d8ce",
    backgroundColor: "#1a202c",
    borderRadius: "6px",
    border: "1px solid #4a5568",
    lineHeight: 1.5,
  },
  manageGrid: {
    display: "grid",
    gap: "1rem",
    marginTop: "1rem",
  },
  manageSection: {
    border: "1px solid #4a5568",
    borderRadius: "8px",
    padding: "1rem",
    backgroundColor: "#1a202c",
  },
  manageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "0.75rem",
  },
  manageTitle: {
    margin: 0,
    color: "white",
    fontSize: "1rem",
  },
  countBadge: {
    color: "#48bb78",
    fontWeight: 900,
    fontSize: "0.78rem",
  },
  dealerActionRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    gap: "0.5rem",
    alignItems: "center",
    marginBottom: "0.75rem",
  },
  inviteList: {
    display: "grid",
    gap: "0.5rem",
  },
  inviteRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "0.75rem",
    padding: "0.65rem",
    border: "1px solid #2d3748",
    borderRadius: "6px",
    backgroundColor: "#111827",
  },
  inviteActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "0.45rem",
    flexWrap: "wrap",
  },
  statusPill: {
    border: "1px solid currentColor",
    borderRadius: "4px",
    padding: "0.2rem 0.4rem",
    fontWeight: 900,
    fontSize: "0.72rem",
    textTransform: "uppercase",
  },
  deleteWarning: {
    margin: 0,
    color: "#d8d8ce",
    lineHeight: 1.55,
  },
  deleteModal: {
    maxWidth: "460px",
    border: "2px solid #f56565",
  },
};
